import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PAYMENT_OPTION_LABELS, PAYMENT_OPTIONS, type OrderCatalogItemDTO, type OrderDetailDTO, type PaymentOption } from '@bifeai/shared';
import { Button, Callout, Dialog, Field, Input, Select, Textarea, useToast } from '@bifeai/ui';
import { Bike, Plus, Search, Store, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { api, errorMessage, isApiError } from '../../lib/api';
import { useCustomers } from '../../lib/customers';
import { formatMoney } from '../../lib/format';
import { useSession } from '../../lib/session';

interface Line {
  product: OrderCatalogItemDTO;
  /** Kilos (con coma) o unidades, como lo escribe la persona. */
  qty: string;
}

/** Hoy, en la hora redonda siguiente (para el campo de fecha y hora). */
function nextHour(): { date: string; time: string } {
  const d = new Date(Date.now() + 3_600_000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return { date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`, time: `${pad(d.getHours())}:00` };
}

const parseQty = (unitType: 'WEIGHT' | 'UNIT', v: string): number | null => {
  const n = Number(v.replace(',', '.'));
  if (!Number.isFinite(n) || n <= 0) return null;
  return unitType === 'WEIGHT' ? Math.round(n * 1000) : Math.round(n);
};
const lineTotal = (l: Line): number => {
  const q = parseQty(l.product.unitType, l.qty) ?? 0;
  return l.product.unitType === 'WEIGHT' ? Math.round((l.product.priceCents * q) / 1000) : l.product.priceCents * q;
};

/**
 * Pedido que el cliente encargó en el mostrador o por teléfono: se carga acá y entra al tablero como cualquier otro.
 * Chau papelito en la pared y cuaderno de encargos.
 */
export function NewOrderDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (o: OrderDetailDTO) => void }) {
  const { me, selectedBranchId } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const own = me.branches.filter((b) => !b.franchise || me.branches.length === 1);
  const [branchId, setBranchId] = useState<string>(selectedBranchId ?? (own.length === 1 ? own[0]!.id : ''));
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [fulfillment, setFulfillment] = useState<'PICKUP' | 'DELIVERY'>('PICKUP');
  const [address, setAddress] = useState('');
  const [fee, setFee] = useState('');
  const start = nextHour();
  const [date, setDate] = useState(start.date);
  const [time, setTime] = useState(start.time);
  const [payment, setPayment] = useState<PaymentOption | ''>('');
  const [notes, setNotes] = useState('');
  const [search, setSearch] = useState('');
  const [lines, setLines] = useState<Line[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Clientes que ya están cargados: al elegir uno se completan nombre y teléfono.
  const people = useCustomers(customerId ? '' : name.trim().length >= 2 ? name : '');
  const suggestions = !customerId && name.trim().length >= 2 ? (people.data ?? []).slice(0, 4) : [];
  const catalog = useQuery({
    queryKey: ['orders-chat', 'catalog', branchId, search.trim()],
    queryFn: () => api.get<OrderCatalogItemDTO[]>(`/orders-chat/catalog?branchId=${branchId}&q=${encodeURIComponent(search.trim())}`),
    enabled: !!branchId,
    placeholderData: (prev) => prev,
  });
  const fixedFee = fulfillment === 'DELIVERY' ? Math.round(Number(fee.replace(',', '.')) * 100) || 0 : 0;
  const total = lines.reduce((n, l) => n + lineTotal(l), 0) + fixedFee;

  const create = useMutation({
    mutationFn: () => {
      const items = lines.map((l) => ({ productId: l.product.id, qty: parseQty(l.product.unitType, l.qty) }));
      return api.post<OrderDetailDTO>('/orders-chat/orders', {
        branchId,
        customerId,
        contactName: name,
        contactPhone: phone.trim() || null,
        fulfillment,
        address: fulfillment === 'DELIVERY' ? address : null,
        deliveryFeeCents: fixedFee,
        scheduledFor: date && time ? new Date(`${date}T${time}:00`).toISOString() : null,
        paymentMethod: payment || null,
        notes: notes.trim() || null,
        items,
      });
    },
    onSuccess: (o) => {
      void qc.invalidateQueries({ queryKey: ['orders-chat'] });
      void qc.invalidateQueries({ queryKey: ['customers'] });
      toast.success(`Pedido #${o.number} cargado`, 'Ya está en el tablero, en Nuevos.');
      onCreated(o);
    },
    onError: (err) => {
      if (isApiError(err) && err.fields) setErrors(err.fields);
      toast.error('No se pudo cargar el pedido', errorMessage(err));
    },
  });

  const submit = () => {
    const e: Record<string, string> = {};
    if (!branchId) e.branchId = 'Elegí la sucursal.';
    if (!name.trim()) e.contactName = 'Escribí el nombre del cliente.';
    if (fulfillment === 'DELIVERY' && !address.trim()) e.address = 'Escribí la dirección de envío.';
    if (!lines.length) e.items = 'Agregá al menos un producto.';
    if (lines.some((l) => parseQty(l.product.unitType, l.qty) === null)) e.items = 'Revisá las cantidades: por ejemplo 1,5 kg o 2 unidades.';
    setErrors(e);
    if (Object.keys(e).length === 0) create.mutate();
  };

  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      wide
      title="Nuevo pedido"
      description="El encargo del mostrador o por teléfono. Entra al tablero como cualquier pedido: chau papelito en la pared."
    >
      <div className="bf-form">
        {own.length > 1 && (
          <Field label="Sucursal" error={errors.branchId}>
            <Select
              value={branchId}
              onChange={(e) => {
                setBranchId(e.target.value);
                setLines([]);
              }}
            >
              <option value="">Elegí la sucursal</option>
              {own.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <div className="bf-form-row">
          <Field label="Cliente" error={errors.contactName}>
            <Input
              value={name}
              autoFocus
              maxLength={120}
              placeholder="Nombre y apellido"
              onChange={(e) => {
                setName(e.target.value);
                setCustomerId(null);
              }}
            />
          </Field>
          <Field label="Teléfono (WhatsApp)" hint="Opcional. Con el teléfono le avisás cuando está listo." error={errors.contactPhone}>
            <Input value={phone} inputMode="tel" maxLength={30} placeholder="11 4477-8888" onChange={(e) => setPhone(e.target.value)} />
          </Field>
        </div>
        {suggestions.length > 0 && (
          <div className="bf-neworder-suggest" aria-label="Clientes cargados">
            {suggestions.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => {
                  setCustomerId(c.id);
                  setName(c.name);
                  setPhone(c.phone ?? '');
                }}
              >
                <strong>{c.name}</strong>
                {c.phone && <span>{c.phone}</span>}
              </button>
            ))}
          </div>
        )}

        <div className="bf-neworder-products">
          <Field label="Productos" error={errors.items}>
            <Input
              value={search}
              placeholder={branchId ? 'Buscar producto (vacío, asado, pollo…)' : 'Primero elegí la sucursal'}
              disabled={!branchId}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Field>
          {branchId && search.trim().length >= 2 && (
            <div className="bf-neworder-results">
              {(catalog.data ?? []).length === 0 ? (
                <p className="bf-cell-sub">{catalog.isFetching ? 'Buscando…' : 'No hay productos con ese nombre.'}</p>
              ) : (
                (catalog.data ?? []).slice(0, 8).map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => {
                      setLines((ls) =>
                        ls.some((l) => l.product.id === p.id) ? ls : [...ls, { product: p, qty: p.unitType === 'WEIGHT' ? '1' : '1' }],
                      );
                      setSearch('');
                    }}
                  >
                    <Search size={14} aria-hidden />
                    <span>{p.name}</span>
                    <span className="bf-cell-sub">
                      {formatMoney(p.priceCents)} {p.unitType === 'WEIGHT' ? 'el kilo' : 'la unidad'}
                    </span>
                    <Plus size={16} aria-hidden />
                  </button>
                ))
              )}
            </div>
          )}
          {lines.length > 0 && (
            <div className="bf-table-wrap">
              <table className="bf-table">
                <thead>
                  <tr>
                    <th>Producto</th>
                    <th style={{ width: 150 }}>Cantidad</th>
                    <th className="bf-num">Importe</th>
                    <th aria-label="Quitar" style={{ width: 40 }} />
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l, i) => (
                    <tr key={l.product.id}>
                      <td>
                        <div className="bf-cell-main">{l.product.name}</div>
                        <div className="bf-cell-sub">
                          {formatMoney(l.product.priceCents)} {l.product.unitType === 'WEIGHT' ? 'el kilo · se pesa al preparar' : 'la unidad'}
                        </div>
                      </td>
                      <td>
                        <div className="bf-neworder-qty">
                          <Input
                            inputMode="decimal"
                            aria-label={`Cantidad de ${l.product.name}`}
                            value={l.qty}
                            onChange={(e) => setLines((ls) => ls.map((x, k) => (k === i ? { ...x, qty: e.target.value } : x)))}
                          />
                          <span>{l.product.unitType === 'WEIGHT' ? 'kg' : 'u.'}</span>
                        </div>
                      </td>
                      <td className="bf-num">
                        {l.product.unitType === 'WEIGHT' ? '≈ ' : ''}
                        {formatMoney(lineTotal(l))}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="bf-icon-btn"
                          aria-label={`Quitar ${l.product.name}`}
                          onClick={() => setLines((ls) => ls.filter((_, k) => k !== i))}
                        >
                          <Trash2 size={16} aria-hidden />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="bf-form-row">
          <Field label="Para cuándo">
            <div className="bf-neworder-when">
              <Input type="date" aria-label="Día" value={date} onChange={(e) => setDate(e.target.value)} />
              <Input type="time" aria-label="Hora" value={time} onChange={(e) => setTime(e.target.value)} />
            </div>
          </Field>
          <Field label="Forma de pago" hint="Opcional.">
            <Select value={payment} onChange={(e) => setPayment(e.target.value as PaymentOption | '')}>
              <option value="">Sin definir</option>
              {PAYMENT_OPTIONS.map((p) => (
                <option key={p} value={p}>
                  {PAYMENT_OPTION_LABELS[p]}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <div className="bf-segmented bf-segmented--text" role="group" aria-label="Entrega">
          <button type="button" aria-pressed={fulfillment === 'PICKUP'} onClick={() => setFulfillment('PICKUP')}>
            <Store aria-hidden /> Retira en el local
          </button>
          <button type="button" aria-pressed={fulfillment === 'DELIVERY'} onClick={() => setFulfillment('DELIVERY')}>
            <Bike aria-hidden /> Envío a domicilio
          </button>
        </div>
        {fulfillment === 'DELIVERY' && (
          <div className="bf-form-row">
            <Field label="Dirección" error={errors.address}>
              <Input value={address} maxLength={300} placeholder="Calle, número, piso" onChange={(e) => setAddress(e.target.value)} />
            </Field>
            <Field label="Costo del envío" hint="Opcional.">
              <Input value={fee} inputMode="decimal" placeholder="$ 0" onChange={(e) => setFee(e.target.value)} />
            </Field>
          </div>
        )}
        <Field label="Notas" hint="Opcional. Por ejemplo: vacío sin grasa, milanesas finitas.">
          <Textarea value={notes} maxLength={500} rows={2} onChange={(e) => setNotes(e.target.value)} />
        </Field>
        {lines.some((l) => l.product.unitType === 'WEIGHT') && (
          <Callout>Lo que se pesa queda con precio aproximado: al prepararlo cargás el peso real y queda el total final.</Callout>
        )}
        <div className="bf-neworder-foot">
          <span>
            {lines.some((l) => l.product.unitType === 'WEIGHT') ? 'Total aproximado' : 'Total'} <strong>{formatMoney(total)}</strong>
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button variant="primary" loading={create.isPending} onClick={submit}>
              Cargar pedido
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
